import IconBigArrowBottom from '@assets/icons/big arrow bottom.svg?react';
import {
 Box,
 FormControl,
 InputLabel,
 MenuItem,
 Select,
 TextField,
 Typography,
 useTheme,
} from '@mui/material';
import {
 getGridDateOperators,
 GridFilterOperator,
} from '@mui/x-data-grid-premium';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateRangeColumn } from './types';

dayjs.extend(weekOfYear);

interface DateRangeFilterProps {
 columns: DateRangeColumn[];
}

const now = dayjs();

const DateRangeFilter = ({ columns }: DateRangeFilterProps) => {
 const fieldName = 'Date';
 const { t } = useTranslation();
 const dateOperators: GridFilterOperator[] = getGridDateOperators();
 const th = useTheme();
 const {
  control,
  setValue,
  getValues,
  formState: { errors },
 } = useFormContext();

 const periods = [
  {
   value: [now.startOf('isoWeek'), now.endOf('isoWeek')],
   label: t('filterSidebar.thisWeek'),
  },
  {
   value: [
    now.subtract(7, 'day').startOf('isoWeek'),
    now.subtract(7, 'day').endOf('isoWeek'),
   ],
   label: t('filterSidebar.lastWeek'),
  },
  {
   value: [now.startOf('month'), now.endOf('month')],
   label: t('filterSidebar.thisMonth'),
  },
  {
   value: [
    now.subtract(1, 'month').startOf('month'),
    now.subtract(1, 'month').endOf('month'),
   ],
   label: t('filterSidebar.lastMonth'),
  },
 ];

 const updateDateRange = (type: 'year' | 'month' | 'week', value: number) => {
  let newStart: Dayjs;
  let newEnd: Dayjs;

  const currentStart = getValues('DateFrom')
   ? dayjs(getValues('DateFrom'))
   : dayjs();
  const currentEnd = getValues('DateTo') ? dayjs(getValues('DateTo')) : dayjs();
  console.log('updateDateRange', type, value);
  switch (type) {
   case 'year':
    if (value < 1900 || value > 2100) return;
    newStart = currentStart.year(value).startOf('year');
    newEnd = currentEnd.year(value).endOf('year');
    break;
   case 'month':
    if (value < 0 || value > 12) return;
    newStart = currentStart.month(value - 1).startOf('month');
    newEnd = currentEnd.month(value - 1).endOf('month');
    break;
   case 'week':
    if (value < 0 || value > 53) return;
    newStart = currentStart.week(value).startOf('week');
    newEnd = currentEnd.week(value).endOf('week');
    break;
   default:
    return;
  }
  console.log('UPDATE VALLL', newStart, newEnd);
  setValue('DateFrom', newStart);
  setValue('DateTo', newEnd);
  setValue('period', '');
  if (!getValues(`operators[${fieldName}]`)) {
   setValue(`operators[${fieldName}]`, 'is');
  }
 };

 return !columns.length ? null : (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mb: 3 }}>
   <Box
    sx={{
     display: 'flex',
     gap: 1,
     justifyContent: 'space-between',
     alignItems: 'center',
     width: '100%',
    }}
   >
    <Typography
     sx={{ fontSize: 14, color: th.palette.kemek.darkGray, fontWeight: 500 }}
    >
     {t('global.date')}
    </Typography>
    <Controller
     name={`operators[${fieldName}]`}
     control={control}
     defaultValue="is"
     render={({ field }) => (
      <TextField
       {...field}
       type="hidden"
       variant="standard"
       sx={{ display: 'none' }}
      />
     )}
    />
   </Box>
   <Controller
    name="DateField"
    control={control}
    defaultValue={columns[0].Name}
    render={({ field }) => (
     <FormControl
      sx={{ flex: 1, display: columns.length > 1 ? 'block' : 'none' }}
     >
      <InputLabel shrink>{t('global.field')}</InputLabel>
      <Select
       {...field}
       displayEmpty
       sx={{ width: '100%' }}
       value={field.value}
       label={t('global.field')}
      >
       {columns.map((value) => (
        <MenuItem key={value.Name} value={value.Name}>
         {value.Translation}
        </MenuItem>
       ))}
      </Select>
     </FormControl>
    )}
   />
   <Box sx={{ flex: 1 }}>
    <Controller
     name="DateFrom"
     control={control}
     render={({ field }) => (
      <DateTimePicker
       views={['year', 'month', 'day', 'hours', 'minutes']}
       {...field}
       slotProps={{
        field: {
         clearable: true,
         onClear: () => setValue('DateFrom', ''),
        },
       }}
       sx={{
        width: '100%',
       }}
       value={field.value ? dayjs(field?.value) : null}
       onChange={(value) => {
        setValue('DateFrom', value);
       }}
       label={t('filterSidebar.dateFrom')}
      />
     )}
    />
   </Box>
   <Box sx={{ flex: 1 }}>
    <Controller
     name="DateTo"
     control={control}
     render={({ field }) => (
      <DateTimePicker
       views={['year', 'month', 'day', 'hours', 'minutes']}
       {...field}
       slotProps={{
        field: {
         clearable: true,
         onClear: () => setValue('DateTo', ''),
        },
       }}
       sx={{
        width: '100%',
       }}
       value={field.value ? dayjs(field?.value) : null}
       onChange={(value) => {
        setValue('DateTo', value);
       }}
       label={t('filterSidebar.dateTo')}
      />
     )}
    />
   </Box>
   <Box sx={{ display: 'flex', justifyContent: 'center' }}>
    <IconBigArrowBottom
     style={{
      color: th.palette.kemek.gray,
     }}
    />
   </Box>
   <Box>
    <FormControl fullWidth>
     <InputLabel id="date-range-select-label">
      {t('filterSidebar.period')}
     </InputLabel>
     <Controller
      name="period"
      defaultValue=""
      control={control}
      render={({ field }) => (
       <Select
        {...field}
        id="date-range-select"
        labelId="date-range-select-label"
        label={t('filterSidebar.period')}
        onChange={(e) => {
         setValue('period', e.target.value);
         setValue('year', '');
         setValue('month', '');
         setValue('week', '');

         if (e.target.value === '') {
          console.log('reset');
          setValue('DateFrom', '');
          setValue('DateTo', '');
          setValue(`operators[${fieldName}]`, '');
         } else {
          console.log('set');
          setValue('DateFrom', periods[Number(e.target.value)].value[0]);
          setValue('DateTo', periods[Number(e.target.value)].value[1]);
         }
         if (!getValues(`operators[${fieldName}]`)) {
          setValue(`operators[${fieldName}]`, 'is');
         }
        }}
        sx={{
         height: '100%',
         background: th.palette.kemek.white,
         width: '100%',
        }}
        // displayEmpty
        inputProps={{ 'aria-label': t('filterSidebar.period') }}
       >
        <MenuItem value="" selected>
         Tuščias
        </MenuItem>
        {periods.map((item, index) => (
         <MenuItem key={item.label} value={index}>
          {item.label}
         </MenuItem>
        ))}
       </Select>
      )}
     />
    </FormControl>
   </Box>
   <Box sx={{ display: 'flex', gap: 1 }}>
    <Controller
     name="year"
     defaultValue=""
     control={control}
     rules={{
      min: { value: 1900, message: 'Year must be at least 1900' },
      max: { value: 2100, message: 'Year must be at most 2100' },
      validate: (value) => (value ? !Number.isNaN(value) : true),
     }}
     render={({ field }) => (
      <TextField
       {...field}
       label={t('global.year')}
       type="number"
       error={!!errors.year}
       helperText={errors.year ? String(errors.year.message) : ''}
       onChange={(e) => {
        const newValue = parseInt(e.target.value, 10);
        if (!Number.isNaN(newValue)) {
         setValue('year', Number.isNaN(newValue) ? '' : newValue);
         if (newValue >= 1970 && newValue <= 2100) {
          updateDateRange('year', newValue);
         }
        }
       }}
       sx={{ flex: 1 }}
       slotProps={{
        htmlInput: {
         step: 1,
         min: 1900,
         max: 2100,
        },
       }}
      />
     )}
    />

    <Controller
     name="month"
     defaultValue=""
     control={control}
     rules={{
      min: { value: 1, message: 'Month must be between 1 and 12' },
      max: { value: 12, message: 'Month must be between 1 and 12' },
      validate: (value) => (value ? !Number.isNaN(value) : true),
     }}
     render={({ field }) => (
      <TextField
       {...field}
       label={t('global.month')}
       type="number"
       error={!!errors.month}
       helperText={errors.month ? String(errors.month.message) : ''}
       onChange={(e) => {
        const newValue = parseInt(e.target.value, 10);
        if (!Number.isNaN(newValue) && newValue >= 1 && newValue <= 12) {
         setValue('month', Number.isNaN(newValue) ? '' : newValue);
         setValue('week', '');
         updateDateRange('month', newValue);
        }
       }}
       sx={{ flex: 1 }}
       slotProps={{
        htmlInput: {
         step: 1,
         min: 1,
         max: 12,
        },
       }}
      />
     )}
    />

    <Controller
     name="week"
     defaultValue=""
     control={control}
     rules={{
      min: { value: 1, message: 'Week must be between 1 and 53' },
      max: { value: 53, message: 'Week must be between 1 and 53' },
      validate: (value) => (value ? !Number.isNaN(value) : true),
     }}
     render={({ field }) => (
      <TextField
       {...field}
       label={t('global.week')}
       type="number"
       error={!!errors.week}
       helperText={errors.week ? String(errors.week.message) : ''}
       onChange={(e) => {
        const newValue = parseInt(e.target.value, 10);
        if (!Number.isNaN(newValue) && newValue >= 1 && newValue <= 53) {
         setValue('week', Number.isNaN(newValue) ? '' : newValue);
         setValue('month', '');
         updateDateRange('week', newValue);
        }
       }}
       sx={{ flex: 1 }}
       slotProps={{
        htmlInput: {
         step: 1,
         min: 1,
         max: 53,
        },
       }}
      />
     )}
    />
   </Box>
  </Box>
 );
};
export default DateRangeFilter;
